import { create } from 'zustand'
import { persist } from 'zustand/middleware'

type Theme = 'light' | 'dark' | 'system'
type ColorScheme = 'green' | 'blue' | 'purple' | 'orange' | 'pink' | 'classic'
type ChatBackground = 'pattern' | 'gradient' | 'solid' | 'waves' | 'dots'

interface UIState {
  theme: Theme
  colorScheme: ColorScheme
  chatBackground: ChatBackground
  sidebarOpen: boolean
  killSwitchConfirmOpen: boolean
  appearanceModalOpen: boolean
  tasksPanelOpen: boolean

  setTheme: (theme: Theme) => void
  setColorScheme: (scheme: ColorScheme) => void
  setChatBackground: (bg: ChatBackground) => void
  toggleSidebar: () => void
  setSidebarOpen: (open: boolean) => void
  setKillSwitchConfirmOpen: (open: boolean) => void
  setAppearanceModalOpen: (open: boolean) => void
  toggleTasksPanel: () => void
  setTasksPanelOpen: (open: boolean) => void
}

// Color scheme definitions - RGB values for CSS variables
export const colorSchemes: Record<ColorScheme, { 
  primary: string
  primaryRgb: string
  primaryLight: string
  primaryLightRgb: string
  primaryDark: string
  gradient: string
  bgGradient: string
  label: string
}> = {
  green: {
    primary: '#4CD137',
    primaryRgb: '76 209 55',
    primaryLight: '#72E05B',
    primaryLightRgb: '114 224 91',
    primaryDark: '#39B54A',
    gradient: 'linear-gradient(135deg, #72E05B 0%, #4CD137 50%, #39B54A 100%)',
    bgGradient: 'linear-gradient(135deg, #0F3D1A 0%, #134422 50%, #0A2B12 100%)',
    label: 'Safaricom Green',
  },
  blue: {
    primary: '#3B82F6',
    primaryRgb: '59 130 246',
    primaryLight: '#60A5FA',
    primaryLightRgb: '96 165 250',
    primaryDark: '#2563EB',
    gradient: 'linear-gradient(135deg, #60A5FA 0%, #3B82F6 50%, #2563EB 100%)',
    bgGradient: 'linear-gradient(135deg, #0F172A 0%, #1E3A5F 50%, #0F172A 100%)',
    label: 'Ocean Blue',
  },
  purple: {
    primary: '#8B5CF6',
    primaryRgb: '139 92 246',
    primaryLight: '#A78BFA',
    primaryLightRgb: '167 139 250',
    primaryDark: '#7C3AED',
    gradient: 'linear-gradient(135deg, #A78BFA 0%, #8B5CF6 50%, #7C3AED 100%)',
    bgGradient: 'linear-gradient(135deg, #1E1033 0%, #2D1F4D 50%, #1E1033 100%)',
    label: 'Royal Purple',
  },
  orange: {
    primary: '#F59E0B',
    primaryRgb: '245 158 11',
    primaryLight: '#FBBF24',
    primaryLightRgb: '251 191 36',
    primaryDark: '#D97706',
    gradient: 'linear-gradient(135deg, #FBBF24 0%, #F59E0B 50%, #D97706 100%)',
    bgGradient: 'linear-gradient(135deg, #1C1208 0%, #3D2A0F 50%, #1C1208 100%)',
    label: 'Sunset Orange',
  },
  pink: {
    primary: '#EC4899',
    primaryRgb: '236 72 153',
    primaryLight: '#F472B6',
    primaryLightRgb: '244 114 182',
    primaryDark: '#DB2777',
    gradient: 'linear-gradient(135deg, #F472B6 0%, #EC4899 50%, #DB2777 100%)',
    bgGradient: 'linear-gradient(135deg, #1F0A14 0%, #3D1528 50%, #1F0A14 100%)',
    label: 'Rose Pink',
  },
  classic: {
    primary: '#5BA057',  // Telegram classic green
    primaryRgb: '91 160 87',
    primaryLight: '#7AC277',
    primaryLightRgb: '122 194 119',
    primaryDark: '#4A8F47',
    gradient: 'linear-gradient(135deg, #7AC277 0%, #5BA057 50%, #4A8F47 100%)',
    bgGradient: 'linear-gradient(135deg, #E4DCD4 0%, #D9D2CA 50%, #E4DCD4 100%)',  // Telegram beige
    label: 'Classic',
  },
}

// Chat background definitions
export const chatBackgrounds: Record<ChatBackground, { style: string; className?: string }> = {
  pattern: {
    style: '',
    className: 'chat-background',
  },
  gradient: {
    style: 'linear-gradient(135deg, rgba(16, 185, 129, 0.1) 0%, rgba(5, 150, 105, 0.05) 100%)',
    className: '',
  },
  solid: {
    style: 'rgba(0, 0, 0, 0.3)',
    className: '',
  },
  waves: {
    style: '',
    className: 'chat-background-waves',
  },
  dots: {
    style: '',
    className: 'chat-background-dots',
  },
}

export const useUIStore = create<UIState>()(
  persist(
    (set) => ({
      theme: 'dark',
      colorScheme: 'green',
      chatBackground: 'pattern',
      sidebarOpen: true,
      killSwitchConfirmOpen: false,
      appearanceModalOpen: false,
      tasksPanelOpen: false,

      setTheme: (theme) => {
        set({ theme })
        applyTheme(theme)
      },

      setColorScheme: (colorScheme) => {
        set({ colorScheme })
        applyColorScheme(colorScheme)
      },

      setChatBackground: (chatBackground) => {
        set({ chatBackground })
      },

      toggleSidebar: () => {
        set((state) => ({ sidebarOpen: !state.sidebarOpen }))
      },

      setSidebarOpen: (open) => {
        set({ sidebarOpen: open })
      },

      setKillSwitchConfirmOpen: (open) => {
        set({ killSwitchConfirmOpen: open })
      },

      toggleTasksPanel: () => {
        set((state) => ({ tasksPanelOpen: !state.tasksPanelOpen }))
      },

      setTasksPanelOpen: (open) => {
        set({ tasksPanelOpen: open })
      },

      setAppearanceModalOpen: (open) => {
        set({ appearanceModalOpen: open })
      },
    }),
    {
      name: 'niki-ui',
      partialize: (state) => ({
        theme: state.theme,
        colorScheme: state.colorScheme,
        chatBackground: state.chatBackground,
        sidebarOpen: state.sidebarOpen,
        tasksPanelOpen: state.tasksPanelOpen,
      }),
      onRehydrateStorage: () => (state) => {
        if (state) {
          applyTheme(state.theme)
          applyColorScheme(state.colorScheme)
        }
      },
    }
  )
)

function applyTheme(theme: Theme) {
  const root = window.document.documentElement

  if (theme === 'system') {
    const systemTheme = window.matchMedia('(prefers-color-scheme: dark)').matches
      ? 'dark'
      : 'light'
    root.classList.toggle('dark', systemTheme === 'dark')
  } else {
    root.classList.toggle('dark', theme === 'dark')
  }
}

function applyColorScheme(scheme: ColorScheme) {
  const root = window.document.documentElement
  const colors = colorSchemes[scheme]
  
  // Set CSS variables that the whole app uses
  root.style.setProperty('--color-primary', colors.primaryRgb)
  root.style.setProperty('--color-primary-light', colors.primaryLightRgb)
  root.style.setProperty('--color-primary-hex', colors.primary)
  root.style.setProperty('--color-primary-light-hex', colors.primaryLight)
  root.style.setProperty('--color-primary-dark-hex', colors.primaryDark)
  root.style.setProperty('--gradient-accent', colors.gradient)
  root.style.setProperty('--gradient-main', colors.bgGradient)
  
  // Update the body/main background
  root.style.setProperty('--bg-gradient', colors.bgGradient)
  
  // Store scheme name for CSS class-based styling
  root.setAttribute('data-color-scheme', scheme)
}

// Listen for system theme changes
if (typeof window !== 'undefined') {
  window
    .matchMedia('(prefers-color-scheme: dark)')
    .addEventListener('change', () => {
      const currentTheme = useUIStore.getState().theme
      if (currentTheme === 'system') {
        applyTheme('system')
      }
    })
}
